import { Check } from 'lucide-react';

const IndustrialPackaging = () => {
  const packagingTypes = [
    {
      title: 'Wooden Boxes',
      description: 'Sturdy wooden boxes designed for safe storage and transportation of industrial goods, electronics, and machinery parts.',
      specs: ['Custom dimensions available', 'Reinforced corners and edges', 'Interior cushioning options', 'ISPM-15 certified for export'],
    },
    {
      title: 'Wooden Crates',
      description: 'Heavy-duty crates with open or closed design, perfect for large equipment and industrial components.',
      specs: ['Load capacity up to 5000 kg', 'Open or closed construction', 'Stackable design', 'Easy assembly and disassembly'],
    },
    {
      title: 'Plywood Boxes',
      description: 'Lightweight yet strong plywood packaging solutions for various products requiring protection during transit.',
      specs: ['Water-resistant plywood', 'Cost-effective solution', 'Multiple size options', 'Suitable for air and sea freight'],
    },
    {
      title: 'Export Packaging',
      description: 'Complete export packaging solutions compliant with international standards for safe overseas shipping.',
      specs: ['ISPM-15 heat treatment', 'Fumigation certificates', 'Moisture barrier options', 'Custom branding available'],
    },
    {
      title: 'Corrugated Boxes',
      description: 'High-quality corrugated packaging for lighter goods, offering excellent protection and cost efficiency.',
      specs: ['Various strength grades', 'Custom printing available', 'Recyclable and eco-friendly', 'Fast delivery'],
    },
    {
      title: 'Dunnage & Supports',
      description: 'Custom dunnage and support structures to secure cargo and prevent movement during transportation.',
      specs: ['Custom-fit design', 'Prevents product damage', 'Optimizes container space', 'Reusable options available'],
    },
  ];

  return (
    <div className="font-inter" data-testid="industrial-packaging-page">
      {/* Header */}
      <section
        className="relative bg-cover bg-center h-96 flex items-center"
        style={{
          backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(https://images.pexels.com/photos/27111449/pexels-photo-27111449.jpeg)'
        }}
        data-testid="industrial-packaging-header"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-white">
          <h1 className="font-poppins font-bold text-4xl md:text-5xl mb-4">
            Industrial Packaging Solutions
          </h1>
          <p className="text-xl text-gray-200">
            Comprehensive packaging solutions for safe storage and export
          </p>
          <div className="w-24 h-1 bg-[#B11217] mt-4"></div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-white" data-testid="industrial-packaging-intro">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-gray-600 leading-relaxed text-lg mb-4">
              Our industrial packaging solutions are designed to protect your valuable goods during storage, handling, and transportation. From wooden boxes to corrugated packaging, we offer a complete range of solutions tailored to your industry needs.
            </p>
            <p className="text-gray-600 leading-relaxed text-lg">
              All our packaging products meet international standards and are export-ready, ensuring your shipments reach their destination safely.
            </p>
          </div>
        </div>
      </section>

      {/* Packaging Types */}
      <section className="py-16 bg-[#F2F2F2]" data-testid="packaging-types-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-poppins font-bold text-3xl text-[#2B2B2B] mb-12 text-center">
            Our Packaging Solutions
          </h2>
          <div className="space-y-8">
            {packagingTypes.map((packaging, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-lg shadow-md hover:shadow-lg transition-shadow"
                data-testid={`packaging-type-${index}`}
              >
                <h3 className="font-poppins font-bold text-2xl text-[#2B2B2B] mb-3">
                  {packaging.title}
                </h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {packaging.description}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {packaging.specs.map((spec, specIndex) => (
                    <div key={specIndex} className="flex items-start gap-2">
                      <Check size={20} className="text-[#B11217] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{spec}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Image */}
      <section className="py-16 bg-white" data-testid="packaging-industries-image">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <img
                src="https://images.pexels.com/photos/1624695/pexels-photo-1624695.jpeg"
                alt="Export and Shipping"
                className="w-full h-auto rounded-lg shadow-lg"
              />
            </div>
            <div>
              <h2 className="font-poppins font-bold text-3xl text-[#2B2B2B] mb-6">
                Export-Ready Packaging
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                Our packaging solutions are specifically designed to meet international shipping requirements, ensuring your products arrive safely at their destination.
              </p>
              <p className="text-gray-600 leading-relaxed">
                We provide complete documentation, including ISPM-15 certificates and fumigation reports, to facilitate smooth customs clearance.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#B11217]" data-testid="industrial-packaging-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="font-poppins font-bold text-3xl mb-4">
            Need Custom Packaging Solutions?
          </h2>
          <p className="text-xl mb-8 text-gray-100">
            Our team can design packaging solutions tailored to your specific requirements.
          </p>
          <a
            href="/contact"
            className="bg-white text-[#B11217] px-8 py-4 font-poppins font-semibold text-sm uppercase tracking-wide hover:bg-gray-100 transition-colors inline-block"
            data-testid="industrial-packaging-cta-button"
          >
            Contact Us Today
          </a>
        </div>
      </section>
    </div>
  );
};

export default IndustrialPackaging;